import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { C } from "../../styles/theme";
import ImgUploadFld from "../../components/common/ImgUploadFld";

export default function ImageManagerTab() {
  const { db, setDB } = useData();
  const { toast } = useOutletContext();
  const [activePage, setActivePage] = useState("home");
  const [addingSlot, setAddingSlot] = useState(null); // {pageKey, slot}
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newSlot, setNewSlot] = useState("");
  const [previewImg, setPreviewImg] = useState(null);
  const [urlError, setUrlError] = useState("");

  const pages = [
    { key: "home", label: "Home", icon: "🏠", color: "#7c3aed", slots: ["Hero Background", "About Section", "Why Choose Us"] },
    { key: "home_slides", label: "Hero Slides", icon: "🎞️", color: "#2557A7", slots: ["Hero Slide 1", "Hero Slide 2", "Hero Slide 3", "Hero Slide 4"] },
    { key: "staffing", label: "Staffing", icon: "👥", color: "#E8593C", slots: ["Hero Banner", "CTA Section", "Team Photo"] },
    { key: "it", label: "Software", icon: "💻", color: "#2557A7", slots: ["Hero Banner", "CTA Section", "Portfolio Banner"] },
    { key: "lab", label: "Lab", icon: "🔬", color: "#0F6E56", slots: ["Hero Banner", "Product Section", "Banner Strip"] },
    { key: "valuation", label: "Valuation", icon: "🏢", color: "#b45309", slots: ["Hero Banner", "CTA Section", "Process Image"] },
    { key: "blog", label: "Blog", icon: "✍️", color: "#0F6E56", slots: ["Page Banner", "Featured Banner"] },
  ];

  const getImages = (pageKey) => (db.settings?.pageImages?.[pageKey] || []);

  const setImages = (pageKey, imgs) => {
    setDB(d => ({ ...d, settings: { ...d.settings, pageImages: { ...(d.settings.pageImages || {}), [pageKey]: imgs } } }));
  };

  const addImage = (pageKey) => {
    if (!newUrl.trim()) { setUrlError("Please upload an image"); return; }
    setUrlError("");
    const imgs = getImages(pageKey);
    const id = pageKey + "-" + Date.now();
    const label = newLabel.trim() || "Image " + (imgs.length + 1);
    const slot = newSlot.trim() || addingSlot?.slot || ("Slot " + (imgs.length + 1));
    setImages(pageKey, [...imgs, { id, slot, url: newUrl.trim(), label }]);
    setNewUrl(""); setNewLabel(""); setNewSlot("");
    setAddingSlot(null);
    toast("Image added to " + label);
  };

  const removeImage = (pageKey, id) => {
    setImages(pageKey, getImages(pageKey).filter(img => img.id !== id));
    toast("Image removed");
  };

  const moveImage = (pageKey, id, dir) => {
    const imgs = [...getImages(pageKey)];
    const idx = imgs.findIndex(i => i.id === id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= imgs.length) return;
    [imgs[idx], imgs[swap]] = [imgs[swap], imgs[idx]];
    setImages(pageKey, imgs);
  };

  const updateImage = (pageKey, id, field, val) => {
    setImages(pageKey, getImages(pageKey).map(img => img.id === id ? { ...img, [field]: val } : img));
  };

  const activePg = pages.find(p => p.key === activePage) || pages[0];
  const imgs = getImages(activePage);

  return (
    <div style={{ maxWidth: 820 }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#0C1F3F,#1A3A6B)", borderRadius: 14, padding: "18px 22px", marginBottom: 18, display: "flex", alignItems: "center", gap: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: -20, top: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,.04)" }} />
        <div style={{ width: 46, height: 46, background: "linear-gradient(135deg,#E8593C,#F2A623)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🖼️</div>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: "#fff", fontWeight: 800, fontSize: 16, margin: 0, fontFamily: "'Playfair Display',Georgia,serif" }}>Page Image Manager</h2>
          <p style={{ color: "rgba(255,255,255,.55)", fontSize: 12.5, marginTop: 3, marginBottom: 0 }}>Add, remove and reorder images for each page. First image in each page = hero banner.</p>
        </div>
        <div style={{ background: "rgba(201,151,42,.15)", border: "1px solid rgba(201,151,42,.3)", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#E8C060", fontWeight: 600, flexShrink: 0 }}>
          {Object.values(db.settings?.pageImages || {}).reduce((a, v) => a + (v?.length || 0), 0)} total images
        </div>
      </div>

      {/* Page tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {pages.map(p => {
          const count = (db.settings?.pageImages?.[p.key] || []).length;
          return (
            <button key={p.key} onClick={() => setActivePage(p.key)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 9, border: "1.5px solid",
              borderColor: activePage === p.key ? p.color : "#e2e8f0",
              background: activePage === p.key ? p.color + "14" : "#fff",
              color: activePage === p.key ? p.color : C.gray,
              cursor: "pointer", fontSize: 12.5, fontWeight: activePage === p.key ? 700 : 400,
              transition: "all .15s"
            }}>
              <span>{p.icon}</span>
              <span>{p.label}</span>
              <span style={{ background: activePage === p.key ? p.color : "#f1f5f9", color: activePage === p.key ? "#fff" : C.gray, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, minWidth: 18, textAlign: "center" }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Active page images */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 13, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,.04)" }}>

        {/* Section header */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", background: `${activePg.color}08` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontSize: 20 }}>{activePg.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.blue }}>{activePg.label} Page — Images</div>
              <div style={{ fontSize: 11.5, color: C.gray, marginTop: 1 }}>First image is used as hero banner · Drag to reorder</div>
            </div>
          </div>
          <button onClick={() => { setAddingSlot({ pageKey: activePage, slot: "" }); setNewUrl(""); setNewLabel(""); setNewSlot(""); }} style={{ background: activePg.color, color: "#fff", border: "none", padding: "8px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: `0 3px 10px ${activePg.color}40` }}>
            + Add Image
          </button>
        </div>

        {/* Slot guide */}
        <div style={{ padding: "10px 18px", background: "#fafbfc", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.gray, fontWeight: 600, marginRight: 4 }}>Available slots:</span>
          {activePg.slots.map(s => (
            <button key={s} onClick={() => { setAddingSlot({ pageKey: activePage, slot: s }); setNewSlot(s); setNewUrl(""); setNewLabel(""); }} style={{ background: "#f1f5f9", border: "1px dashed #d1d5db", color: C.blue, fontSize: 11, padding: "3px 10px", borderRadius: 20, cursor: "pointer", fontWeight: 500, transition: "all .15s" }}>
              + {s}
            </button>
          ))}
        </div>

        {/* Add image form */}
        {addingSlot && addingSlot.pageKey === activePage && (
          <div style={{ padding: "16px 18px", background: "#f0f7ff", borderBottom: "1px solid #bfdbfe", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.blue, marginBottom: 2 }}>
              Add image {newSlot ? `to "${newSlot}" slot` : ""}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.blue, marginBottom: 4, letterSpacing: .3 }}>SLOT / POSITION</label>
                <input value={newSlot} onChange={e => setNewSlot(e.target.value)} placeholder="e.g. Hero Banner" style={{ width: "100%", padding: "8px 11px", border: "1.5px solid #bfdbfe", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box", background: "#fff" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.blue, marginBottom: 4, letterSpacing: .3 }}>IMAGE LABEL</label>
                <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Team photo" style={{ width: "100%", padding: "8px 11px", border: "1.5px solid #bfdbfe", borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box", background: "#fff" }} />
              </div>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.blue, marginBottom: 4, letterSpacing: .3 }}>IMAGE *</label>
              <ImgUploadFld value={newUrl} onChange={v => { setNewUrl(v); setUrlError(""); }} w={100} h={68} maxDim={1400} />
              {urlError && <div style={{ fontSize: 11, color: "#ef4444", marginTop: -6, marginBottom: 6 }}>{urlError}</div>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => addImage(activePage)} style={{ background: activePg.color, color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Add Image</button>
              <button onClick={() => { setAddingSlot(null); setUrlError(""); }} style={{ background: "#f1f5f9", color: C.gray, border: "1px solid #e2e8f0", padding: "8px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Image grid */}
        {imgs.length === 0 ? (
          <div style={{ padding: "48px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 42, marginBottom: 12, opacity: .4 }}>🖼️</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.blue, marginBottom: 6 }}>No images yet for {activePg.label}</div>
            <div style={{ fontSize: 13, color: C.gray, marginBottom: 16 }}>Add images using the button above. The first image becomes the hero banner.</div>
            <button onClick={() => { setAddingSlot({ pageKey: activePage, slot: "" }); setNewUrl(""); setNewLabel(""); setNewSlot(""); }} style={{ background: activePg.color, color: "#fff", border: "none", padding: "10px 22px", borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              + Add First Image
            </button>
          </div>
        ) : (
          <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
            {imgs.map((img, idx) => (
              <div key={img.id} style={{ display: "flex", gap: 12, alignItems: "stretch", background: idx === 0 ? "#fffbeb" : "#f9fafb", border: `1px solid ${idx === 0 ? "#fde68a" : "#e2e8f0"}`, borderRadius: 10, overflow: "hidden", transition: "all .15s" }}>
                {/* Image thumbnail */}
                <div style={{ width: 100, minHeight: 70, flexShrink: 0, cursor: "pointer", position: "relative", overflow: "hidden", background: "#e5e7eb" }} onClick={() => setPreviewImg(img)}>
                  <img src={img.url} alt={img.label} style={{ width: "100%", height: "100%", objectFit: "cover", minHeight: 70, display: "block" }} onError={e => { e.target.style.opacity = ".3"; }} />
                  {idx === 0 && <div style={{ position: "absolute", top: 5, left: 5, background: "#C9972A", color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10, letterSpacing: .3 }}>HERO</div>}
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "all .15s" }} className="img-overlay">
                    <span style={{ background: "rgba(0,0,0,.5)", color: "#fff", fontSize: 11, padding: "3px 8px", borderRadius: 6 }}>Preview</span>
                  </div>
                </div>
                {/* Image info */}
                <div style={{ flex: 1, padding: "10px 4px 10px 0", minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input value={img.label} onChange={e => updateImage(activePage, img.id, "label", e.target.value)} style={{ width: "100%", padding: "5px 8px", border: "1.5px solid #e2e8f0", borderRadius: 6, fontSize: 13, fontWeight: 600, outline: "none", background: "transparent", color: C.blue, boxSizing: "border-box" }} />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <select value={img.slot} onChange={e => updateImage(activePage, img.id, "slot", e.target.value)} style={{ padding: "5px 8px", border: "1.5px solid #e2e8f0", borderRadius: 6, fontSize: 11, background: "#f8fafc", color: C.gray, outline: "none", cursor: "pointer" }}>
                        <option value={img.slot}>{img.slot}</option>
                        {activePg.slots.filter(s => s !== img.slot).map(s => <option key={s} value={s}>{s}</option>)}
                        <option value="Custom">Custom</option>
                      </select>
                    </div>
                  </div>
                  <ImgUploadFld value={img.url} onChange={v => updateImage(activePage, img.id, "url", v)} showPreview={false} maxDim={1400} buttonLabel="Replace Image" />
                </div>
                {/* Controls */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 4, padding: "8px 10px 8px 4px", flexShrink: 0 }}>
                  <button onClick={() => moveImage(activePage, img.id, -1)} disabled={idx === 0} style={{ width: 28, height: 28, border: "1px solid #e2e8f0", borderRadius: 6, background: "#f8fafc", cursor: idx === 0 ? "not-allowed" : "pointer", opacity: idx === 0 ? .35 : 1, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }} title="Move up">▲</button>
                  <button onClick={() => moveImage(activePage, img.id, 1)} disabled={idx === imgs.length - 1} style={{ width: 28, height: 28, border: "1px solid #e2e8f0", borderRadius: 6, background: "#f8fafc", cursor: idx === imgs.length - 1 ? "not-allowed" : "pointer", opacity: idx === imgs.length - 1 ? .35 : 1, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }} title="Move down">▼</button>
                  <button onClick={() => removeImage(activePage, img.id)} style={{ width: 28, height: 28, border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", cursor: "pointer", fontSize: 13, color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }} title="Remove">✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer save bar */}
        {imgs.length > 0 && (
          <div style={{ padding: "12px 18px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fafbfc" }}>
            <span style={{ fontSize: 12, color: C.gray }}>{imgs.length} image{imgs.length !== 1 ? "s" : ""} · first image is the hero banner</span>
            <button onClick={() => toast(`${activePg.label} images saved`)} style={{ background: activePg.color, color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>Save {activePg.label} Images</button>
          </div>
        )}
      </div>

      {/* Full-screen preview modal */}
      {previewImg && (
        <div onClick={() => setPreviewImg(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, overflow: "hidden", maxWidth: 860, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 32px 80px rgba(0,0,0,.5)" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.blue }}>{previewImg.label}</div>
                <div style={{ fontSize: 11, color: C.gray, marginTop: 1 }}>{previewImg.slot}</div>
              </div>
              <button onClick={() => setPreviewImg(null)} style={{ width: 32, height: 32, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: "hidden", background: "#f1f5f9" }}>
              <img src={previewImg.url} alt={previewImg.label} style={{ width: "100%", height: "100%", objectFit: "contain", maxHeight: "70vh", display: "block" }} />
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #f1f5f9", fontSize: 11, color: C.gray, wordBreak: "break-all", fontFamily: "monospace" }}>{previewImg.url}</div>
          </div>
        </div>
      )}
    </div>
  );
}
